import { useCallback, useEffect, useRef, useState } from "react";

import CodeBlock from "./CodeBlock.jsx";
import Icon from "./Icon.jsx";
import { Tab, Tabs } from "./Tabs.jsx";

// <MotorDemo src="motor-hotel.html" html={`…`} js={`…`} />
//
// Formulário do motor rodando de verdade, com o código que o produz ao lado.
//
// O `src` é um arquivo de public/demos/, servido estaticamente e carregado em
// iframe. As demos são HTML puro de propósito: é exatamente o que o cliente vai
// colar no site dele, e reescrevê-las em React esconderia o que ele precisa
// copiar. O iframe também isola o CSS do motor do CSS desta documentação — as
// duas folhas usam nomes de classe genéricos (`.campos`, `.tabs`) e sem a
// barreira uma quebraria a outra.

// Fonte da página inteira, buscada só quando a aba é aberta: o <Tabs> renderiza
// apenas o painel ativo, então quem nunca clica em "Página completa" não paga o
// download.
function FullPageSource({ url }: { url: string }) {
  const [source, setSource] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => !cancelled && setSource(text))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (failed) {
    return (
      <p className="doc-p">
        Não foi possível carregar a fonte. Abra <a href={url}>{url}</a> direto.
      </p>
    );
  }
  if (source === null) return <div className="motor-demo-loading">Carregando…</div>;

  return (
    <CodeBlock>
      <code className="language-html">{source}</code>
    </CodeBlock>
  );
}

function Preview({ src, height, title }: { src: string; height: number; title: string }) {
  const container = useRef<HTMLDivElement | null>(null);
  const [full, setFull] = useState(false);

  // O estado de tela cheia não é nosso: o usuário sai dela com Esc, sem passar
  // pelo botão. Quem manda é o evento do documento.
  useEffect(() => {
    const sync = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const toggle = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen();
    else container.current?.requestFullscreen();
  }, []);

  return (
    <div className={`motor-demo ${full ? "full" : ""}`} ref={container}>
      <iframe
        className="motor-demo-frame"
        src={src}
        title={title}
        height={full ? "100%" : height}
        loading="lazy"
        allowFullScreen
      />
      <button
        type="button"
        className="motor-demo-expand"
        onClick={toggle}
        aria-label={full ? "Sair da tela cheia" : "Abrir em tela cheia"}
      >
        <Icon name={full ? "close" : "external"} size={13} />
      </button>
    </div>
  );
}

interface MotorDemoProps {
  src: string;
  height?: number;
  title?: string;
  html?: string;
  js?: string;
}

export default function MotorDemo({ src, height = 500, title = "Motor", html, js }: MotorDemoProps) {
  const url = `/demos/${src}`;

  return (
    <Tabs>
      <Tab label="Preview">
        <Preview src={url} height={height} title={title} />
      </Tab>
      {html && (
        <Tab label="HTML">
          <CodeBlock>
            <code className="language-html">{html}</code>
          </CodeBlock>
        </Tab>
      )}
      {js && (
        <Tab label="JavaScript">
          <CodeBlock>
            <code className="language-javascript">{js}</code>
          </CodeBlock>
        </Tab>
      )}
      <Tab label="Página completa">
        <FullPageSource url={url} />
      </Tab>
    </Tabs>
  );
}
